import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Lock, Unlock, IndianRupee, Eye, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';
import { Navigate } from 'react-router-dom';

interface CourierRow {
  id: number; name: string; code: string; brand?: string | null; orderType: string;
  hasPricing: boolean;
  pricing: Array<{ orderType: string; effectiveFrom: string; updatedAt: string }>;
}
interface PricingRow {
  id: number; courierId: number; orderType: 'B2C' | 'B2B'; rates: any;
  effectiveFrom: string; isActive: boolean;
}

export function CourierPricingPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.kind === 'user' && user.userType === 'SUPER_ADMIN';
  if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;

  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [viewCourierId, setViewCourierId] = useState<number | null>(null);
  const [rates, setRates] = useState<PricingRow[] | null>(null);

  const { data: summary = [], refetch } = useQuery({
    queryKey: ['courier-pricing-summary'],
    queryFn: async () => (await api.get<CourierRow[]>('/courier-pricing/summary')).data,
  });

  const verify = useMutation({
    mutationFn: async (pw: string) => (await api.post('/courier-pricing/verify-password', { password: pw })).data,
    onSuccess: () => { setUnlocked(true); toast.success('Unlocked'); },
    onError: () => toast.error('Incorrect password'),
  });

  const viewRates = useMutation({
    mutationFn: async (courierId: number) => (await api.post<PricingRow[]>('/courier-pricing/view', { courierId, password })).data,
    onSuccess: (rows, courierId) => { setRates(rows); setViewCourierId(courierId); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to load rates'),
  });

  if (!unlocked) {
    return (
      <div className="p-6 max-w-md mx-auto">
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow">
          <div className="flex items-center gap-2 mb-3">
            <Lock size={20} className="text-emerald-700" />
            <h1 className="text-xl font-bold">Courier Pricing — Restricted</h1>
          </div>
          <p className="text-sm text-slate-600 mb-4">These are our internal cost prices from each courier. Visible to <b>SUPER_ADMIN</b> only. Enter your password to continue.</p>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Your admin password"
            className="w-full border rounded px-3 py-2 text-sm mb-3"
            onKeyDown={(e) => { if (e.key === 'Enter') verify.mutate(password); }} />
          <button onClick={() => verify.mutate(password)} disabled={!password || verify.isPending}
            className="w-full px-3 py-2 bg-emerald-600 text-white rounded text-sm disabled:opacity-50">
            {verify.isPending ? 'Verifying…' : 'Unlock'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <IndianRupee size={22} className="text-emerald-700" /> Courier Cost Pricing
          <span className="text-xs font-normal text-emerald-700 inline-flex items-center gap-1 ml-3"><Unlock size={12} /> Unlocked</span>
        </h1>
        <button onClick={() => refetch()} className="px-3 py-2 border rounded text-sm flex items-center gap-1"><RefreshCcw size={14} /> Refresh</button>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-5 bg-white border rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-emerald-50 text-sm font-medium">Couriers</div>
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50"><tr><th className="px-3 py-2 text-left">Courier</th><th className="px-3 py-2 text-left">Pricing</th><th></th></tr></thead>
            <tbody>
              {summary.map((c) => (
                <tr key={c.id} className="border-t">
                  <td className="px-3 py-2 text-sm">{c.name} <span className="text-slate-400 text-xs">· {c.code}</span></td>
                  <td className="px-3 py-2 text-xs">
                    {c.hasPricing
                      ? c.pricing.map((p) => <span key={p.orderType} className="inline-block mr-1 bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">{p.orderType}</span>)
                      : <span className="text-slate-400">none</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => viewRates.mutate(c.id)} className="text-emerald-700 hover:underline inline-flex items-center gap-1 text-xs">
                      <Eye size={12} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="col-span-7 bg-white border rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-emerald-50 text-sm font-medium">
            Rates {viewCourierId && summary.find((c) => c.id === viewCourierId) ? `— ${summary.find((c) => c.id === viewCourierId)?.name}` : ''}
          </div>
          {!rates && <div className="p-6 text-sm text-slate-400 text-center">Click "View" on a courier to load its rate matrix</div>}
          {rates && rates.length === 0 && <div className="p-6 text-sm text-slate-400 text-center">No pricing configured for this courier yet. Use the form below to add it.</div>}
          {rates && rates.map((r) => (
            <div key={r.id} className="p-3 border-b">
              <div className="text-xs text-slate-500 mb-1">{r.orderType} · Effective {r.effectiveFrom?.slice(0, 10)}</div>
              <pre className="bg-slate-50 border rounded p-2 overflow-auto text-xs max-h-72">{JSON.stringify(r.rates, null, 2)}</pre>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
